import type { ReactionType } from '@/shared/types/tables';

export type ReactionCounts = Record<ReactionType, number>;

export interface ReactionSummary {
  counts: ReactionCounts;
  /** The current user's reaction (one per article), or null. */
  mine: ReactionType | null;
}

export function emptyReactionCounts(): ReactionCounts {
  return { useful: 0, eye: 0, share: 0, confused: 0 };
}

/**
 * Next summary after the current user clicks `type`. Pure so the optimistic
 * update and its expectations are testable in isolation.
 *
 * `remove` is decided by the caller from the *rendered* state — never from the
 * cache, which the optimistic update has already mutated by the time the
 * mutation runs (that mismatch once turned every reaction into a no-op delete).
 * Only one reaction per user per article exists, so selecting a new type moves
 * the count rather than adding a second one.
 */
export function applyReactionToggle(
  summary: ReactionSummary | undefined,
  type: ReactionType,
  remove: boolean,
): ReactionSummary {
  const base = summary ?? { counts: emptyReactionCounts(), mine: null };
  const counts = { ...base.counts };

  if (base.mine) counts[base.mine] = Math.max(0, counts[base.mine] - 1);
  if (remove) return { counts, mine: null };

  counts[type] += 1;
  return { counts, mine: type };
}
